
import React, { useRef, useState, useEffect, forwardRef } from 'react';
import { FaPlay, FaPause } from "react-icons/fa";

const CustomVideoPlayer = forwardRef(({ src, markers = [], onTimeUpdate, autoPlay = false, title }, ref) => {
    const internalVideoRef = useRef(null);
    const videoRef = ref || internalVideoRef;

    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [isHovering, setIsHovering] = useState(false);

    const [duration, setDuration] = useState(0);

    useEffect(() => {
        if (src && videoRef.current) {
            // Reset state on new source
            setIsPlaying(false);
            setCurrentTime(0);
            setDuration(0);
        }
    }, [src, videoRef]);

    const togglePlay = () => {
        if (videoRef.current) {
            if (videoRef.current.paused) {
                videoRef.current.play();
                setIsPlaying(true);
            } else {
                videoRef.current.pause();
                setIsPlaying(false);
            }
        }
    };

    const handleTimeUpdate = (e) => {
        const current = e.target.currentTime;
        setCurrentTime(current);
        if (onTimeUpdate) {
            onTimeUpdate(e);
        }
    };

    const handleSeek = (e) => {
        if (!videoRef.current || !duration) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percentage = Math.max(0, Math.min(1, x / rect.width));
        videoRef.current.currentTime = percentage * duration;
    };

    const handleEnded = () => {
        setIsPlaying(false);
    };

    const formatTime = (time) => {
        if (isNaN(time) || time === Infinity) return "00:00";
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    return (
        <div
            className="relative w-4/5 h-4/5 rounded-lg overflow-hidden group bg-black"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
        >
            <video
                ref={videoRef}
                src={src?.src || src} // Handle both object with {src, type} and simple string
                className="w-full h-full cursor-pointer m-auto"
                onClick={togglePlay}
                onTimeUpdate={handleTimeUpdate}
                onEnded={handleEnded}
                onLoadedMetadata={(e) => {
                    const video = e.target;
                    if (video.duration === Infinity) {
                        video.currentTime = 1e101;
                        video.ontimeupdate = function () {
                            this.ontimeupdate = null;
                            this.currentTime = 0;
                            setDuration(this.duration);
                        };
                    } else {
                        setDuration(video.duration);
                    }
                }}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                itemType={src?.type}
                autoPlay
            >
                Your browser does not support the video tag.
            </video>

            {/* Overlay Title */}
            {/* {title && isHovering && (
                <div className="absolute top-0 left-0 w-full p-4 bg-gradient-to-b from-black/70 to-transparent text-white transition-opacity duration-300 pointer-events-none">
                    <h3 className="text-lg font-medium">{title}</h3>
                </div>
            )} */}

            {/* Controls Container */}
            <div className={`absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/80 to-transparent p-4 transition-opacity duration-300 ${isHovering || !isPlaying ? 'opacity-100' : 'opacity-0'}`}>

                {/* Progress Bar Container */}
                <div
                    className="relative w-full h-2 bg-gray-600 rounded-full cursor-pointer mb-4 group/progress"
                    onClick={handleSeek}
                >
                    {/* Progress Fill */}
                    <div
                        className="absolute top-0 left-0 h-full bg-cyan-500 rounded-full transition-all duration-100"
                        style={{ width: `${(currentTime / duration) * 100}%` }}
                    />

                    {/* Custom Markers / Breaks */}
                    {duration > 0 && markers.map((marker, index) => {
                        const time = typeof marker === 'object' ? marker.time / 1000 : marker / 1000;
                        const position = (time / duration) * 100;
                        if (position < 0 || position > 100) return null;

                        return (
                            <div
                                key={index}
                                className="absolute top-0 h-full w-1 bg-white z-10 transform -translate-x-1/2"
                                style={{ left: `${position}%` }}
                                title={typeof marker === 'object' ? marker.label : `Chapter ${index + 1}`}
                            />
                        );
                    })}

                    {/* Scrubber Knob (visible on hover) */}
                    <div
                        className="absolute top-1/2 h-4 w-4 bg-white rounded-full shadow transform -translate-y-1/2 -translate-x-1/2 opacity-0 group-hover/progress:opacity-100 transition-opacity"
                        style={{ left: `${(currentTime / duration) * 100}%` }}
                    />

                </div>

                {/* Controls Row */}
                <div className="flex items-center justify-between text-white">
                    <div className="flex items-center gap-4">
                        <button onClick={togglePlay} className="hover:text-cyan-400 transition-colors">
                            {isPlaying ? <FaPause size={20} /> : <FaPlay size={20} />}
                        </button>
                        <div className="text-sm font-medium">
                            <span>{formatTime(currentTime)}</span>
                            <span className="mx-1">/</span>
                            <span>{formatTime(duration)}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Big Play Button Overlay (when paused) */}
            {!isPlaying && (
                <div
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-black/40 rounded-full p-4 cursor-pointer hover:scale-110 transition-transform"
                    onClick={togglePlay}
                >
                    <FaPlay size={30} className="text-white ml-1" />
                </div>
            )}
        </div>
    );
});

export default CustomVideoPlayer;
